import React, { useEffect } from "react";
import { useBranding } from "../context/BrandingContext";

/**
 * SEO helper — updates document meta tags. Avoids react-helmet quirks
 * with React 19.
 *
 * Phase 18.15.4 — title now respects the admin-managed
 * ``browser_title_template`` from /api/website/branding metadata. If
 * the template contains ``{page_title}`` it's substituted; otherwise
 * the page title is used verbatim.
 *
 * Description falls back to ``default_meta_description`` from
 * branding metadata when no per-page description is provided.
 */
export default function SEO({ title, description, keywords, image, noindex = false }) {
  const { metadata } = useBranding();

  useEffect(() => {
    const template = metadata?.browser_title_template || "{page_title} | 5999Cargo";
    const siteTitle = metadata?.site_title || "5999Cargo Group Europe";
    let effectiveTitle = "";
    if (title) {
      // SEO Mini-Sprint (Feb 2026) — guard against double suffix when a
      // page passes a title that already ends in one of the known brand
      // suffixes ("| 5999Cargo", "— 5999Cargo", "| 5999Cargo Group
      // Europe", "— 5999Cargo Group Europe").
      const renderedSuffix = template.includes("{page_title}")
        ? template.replace("{page_title}", "").trim()  // typically "| 5999Cargo"
        : "";
      const shortBrand = "5999Cargo";
      const suffixVariants = [
        renderedSuffix,
        `| ${shortBrand}`,
        `— ${shortBrand}`,
        `| ${siteTitle}`,
        `— ${siteTitle}`,
      ].filter(Boolean).map((s) => s.trim());
      const trimmed = title.trim();
      const alreadyHasSuffix = suffixVariants.some((s) => trimmed.endsWith(s));
      if (alreadyHasSuffix || !template.includes("{page_title}")) {
        effectiveTitle = trimmed;
      } else {
        effectiveTitle = template.replace("{page_title}", trimmed);
      }
    } else {
      // No page title supplied — use the site default
      const tagline = metadata?.site_tagline;
      effectiveTitle = tagline ? `${siteTitle} — ${tagline}` : siteTitle;
    }
    if (effectiveTitle) document.title = effectiveTitle;

    const effectiveDescription =
      description || metadata?.default_meta_description || "";

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", effectiveDescription);
    setMeta("keywords", keywords);
    setMeta("og:title", effectiveTitle, "property");
    setMeta("og:description", effectiveDescription, "property");
    setMeta("og:type", "website", "property");
    if (image) setMeta("og:image", image, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", effectiveTitle);
    setMeta("twitter:description", effectiveDescription);

    let robotsEl = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robotsEl) {
        robotsEl = document.createElement("meta");
        robotsEl.setAttribute("name", "robots");
        document.head.appendChild(robotsEl);
      }
      robotsEl.setAttribute("content", "noindex, nofollow");
    } else if (robotsEl) {
      robotsEl.setAttribute("content", "index, follow");
    }

    // SEO Q11 — automatic canonical tag based on current pathname.
    // Strips query params (?utm_, ?source_, ?ref_, etc.) and trailing
    // slashes. Skipped for noindex pages to avoid telling Google the
    // canonical of a noindex page is itself (mixed signals).
    //
    // SEO Sprint 2 (Feb 2026) — alias canonical-mapping: legacy NL
    // aliases blijven publiek bereikbaar, maar de canonical wijst naar
    // de primaire (EN/short) URL. og:url volgt de canonical. Geen
    // redirect — alleen canonical, want we willen 200 OK behouden voor
    // deeplinks en backlinks vanuit oude e-mails / printmateriaal.
    try {
      const origin = (process.env.REACT_APP_PUBLIC_ORIGIN
        || "https://www.5999cargo.nl").replace(/\/$/, "");
      let path = window.location.pathname || "/";
      // Normalise: remove trailing slash except for root
      if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
      // Apply alias → primary mapping (case-insensitive on the pathname).
      const ALIAS_TO_PRIMARY = {
        "/luchtvracht": "/air-freight",
        "/zeevracht": "/sea-freight",
        "/verhuizing": "/moving",
        "/zakelijke-zendingen": "/zakelijk",
        "/my5999cargo": "/my5999",
        // SEO Mini-Sprint (Feb 2026) — /invoice-generator is an English
        // alias for /factuur-maken; both serve the same React page.
        // Canonical points to the NL primary on the .nl domain.
        "/invoice-generator": "/factuur-maken",
      };
      const canonicalPath = ALIAS_TO_PRIMARY[path.toLowerCase()] || path;
      const canonicalUrl = `${origin}${canonicalPath}`;
      let canonicalEl = document.querySelector('link[rel="canonical"]');
      if (noindex) {
        // For noindex pages, remove any canonical to avoid mixed signals
        if (canonicalEl) canonicalEl.remove();
      } else {
        if (!canonicalEl) {
          canonicalEl = document.createElement("link");
          canonicalEl.setAttribute("rel", "canonical");
          document.head.appendChild(canonicalEl);
        }
        canonicalEl.setAttribute("href", canonicalUrl);
        // og:url mirrors canonical
        setMeta("og:url", canonicalUrl, "property");
      }
    } catch (_) {
      // best-effort — never break the page over canonical
    }
  }, [title, description, keywords, image, noindex, metadata]);

  return null;
}
